/**
 * AppHeader — persistent product header for the app shell.
 *
 * G2: Every route previously looked identical up top (only a conditional
 * work-session debug strip). This header gives constant orientation:
 *   - the current PAGE TITLE, derived from the route (terminology-aware), and
 *   - the ACTIVE PROJECT name, from ProjectContext.
 *
 * The work-session controls (WorkSessionBar) still render independently and are
 * demoted to a compact top-right position via CSS — their behavior is untouched.
 */

import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useProject } from '../../contexts/ProjectContext';
import { useTerminology } from '../../hooks/useTerminology';
import { getNavigationPhases, getAdditionalNavGroups } from '../Navigation/navigationConfig';
import './AppHeader.css';

/**
 * Routes that are not part of the primary navigation config but still need a
 * human-readable title. Values may be functions of the terminology map.
 */
function extraTitles(t: ReturnType<typeof useTerminology>['t']): Record<string, string> {
  return {
    // sysview-wave2 — '/' and '/components' both redirect into the Interlock
    // shell now (which has its own chrome, no AppHeader); these two entries only
    // cover the pre-redirect flash. Kept so that flash never shows a blank title.
    '/': 'Block Diagram',
    '/components': 'Block Diagram',
    '/cause-effect': 'Cause & Effect',
    '/risk-assessment': 'Risk Assessment',
    '/risk-dashboard': 'Risk Dashboard',
    '/visualizations': 'Visualizations',
    '/import': 'Import Data',
    '/completeness': 'Completeness',
    '/audit': 'Audit Trail',
    '/notifications': 'Notifications',
    '/templates': 'Templates',
    '/template-library': 'Template Library',
    '/requirements': `${t.requirements}`,
    '/coverage-matrix': 'Coverage Matrix',
    '/traceability-report': 'Traceability Report',
    '/mitigations': t.mitigations,
    '/error-paths': 'Error Paths',
    '/system-states': 'System States',
    '/failure-mode-templates': `${t.failureMode} Templates`,
    '/reports': 'Reports',
    '/reports/history': 'Report History',
    '/reports/new': 'New Report',
    '/software-logic-workshop': 'Software Logic Workshop',
    '/stress-library': 'Stress Library',
    '/risk-overview': 'Risk Overview',
    '/causes-workshop': `${t.rootCause} Workshop`,
    '/function-workshop': `${t.derivedFunction} Workshop`,
    '/fm-workshop': `${t.failureMode} Workshop`,
    '/mitigations-workshop': `${t.mitigation} Workshop`,
    '/mission': 'Project Mission',
    '/workshop-report': 'Workshop Summary',
    '/subsystem-risk-report': 'Sub-System Risk Report',
  };
}

/**
 * Detect the Apple platform so we can show the correct command-palette modifier
 * hint (⌘K on macOS/iOS, Ctrl+K everywhere else). Computed once at module load;
 * falls back to Ctrl on any non-browser / unknown environment.
 */
const IS_APPLE_PLATFORM =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad|iPod/i.test(
    // `userAgentData.platform` is the modern hint; fall back to platform/UA.
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ||
      navigator.platform ||
      navigator.userAgent ||
      ''
  );

export interface AppHeaderProps {
  /**
   * UF-3: opens the Cmd+K command palette / global search. Wired to the same
   * handler as the keyboard shortcut so the visible affordance and the hotkey
   * are interchangeable.
   */
  onOpenCommandPalette?: () => void;
}

export const AppHeader: React.FC<AppHeaderProps> = ({ onOpenCommandPalette }) => {
  const location = useLocation();
  const { activeProject } = useProject();
  const { t } = useTerminology();

  const pageTitle = useMemo(() => {
    const path = location.pathname;

    // 1) Prefer the label from the navigation config (already terminology-aware)
    const phases = getNavigationPhases(t);
    const groups = getAdditionalNavGroups(t);
    for (const phase of phases) {
      const hit = phase.items.find((i) => i.path === path);
      if (hit) return hit.label;
    }
    for (const group of groups) {
      const hit = group.items.find((i) => i.path === path);
      if (hit) return hit.label;
    }

    // 2) Fall back to the extra-titles map
    const extra = extraTitles(t);
    if (extra[path]) return extra[path];

    // 3) Prefix-match dynamic routes (e.g. /reports/:jobId)
    const prefixHit = Object.keys(extra)
      .filter((p) => p !== '/' && path.startsWith(p))
      .sort((a, b) => b.length - a.length)[0];
    if (prefixHit) return extra[prefixHit];

    // 4) Last resort: title-case the first path segment
    const seg = path.split('/').filter(Boolean)[0];
    if (!seg) return 'Home';
    return seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }, [location.pathname, t]);

  return (
    <header className="app-header" data-testid="app-header">
      <div className="app-header__titles">
        <h1 className="app-header__page-title" data-testid="app-header-page-title">
          {pageTitle}
        </h1>
        <div className="app-header__context" data-testid="app-header-project">
          <span className="app-header__context-label">Project</span>
          <span className="app-header__context-sep" aria-hidden="true">
            /
          </span>
          <span className="app-header__project-name">
            {activeProject ? activeProject.name : 'No project selected'}
          </span>
        </div>
      </div>

      {/* UF-3: always-visible search affordance. Opens the Cmd+K command
          palette (global entity search). Same handler as the keyboard shortcut. */}
      <button
        type="button"
        className="app-header__search"
        onClick={onOpenCommandPalette}
        data-testid="app-header-search"
        aria-label="Open search (command palette)"
        aria-keyshortcuts={IS_APPLE_PLATFORM ? 'Meta+K' : 'Control+K'}
      >
        <span className="app-header__search-icon" aria-hidden="true">
          🔍
        </span>
        <span className="app-header__search-label">Search…</span>
        <kbd className="app-header__search-kbd">{IS_APPLE_PLATFORM ? '⌘K' : 'Ctrl+K'}</kbd>
      </button>
    </header>
  );
};

export default AppHeader;
